import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  adminGetUser,
  adminDeactivateUser,
  adminReactivateUser,
  adminDeleteUser,
  adminResendInvite,
  adminChangeRole,
  adminListTenants,
} from "../api/client";
import type { AdminUser, AdminTenant } from "../api/types";
import { useToast } from "../context/ToastContext";
import { RoleEditor, RoleDisplayBadges, rolesFromEditor, type RoleEditorValue } from "./RoleEditor";
import { useAuth } from "../auth/useAuth";

function stateInfo(user: AdminUser) {
  // USER_STATE_INITIAL = login via magic link (non ha mai impostato password Zitadel).
  // Al primo login riuscito il metadata `invite_status` viene RIMOSSO (non impostato a
  // "active" — vedi _clear_invite_status in magic_link.py), quindi va trattato come
  // "Invitato" SOLO se e' esplicitamente "pending", non quando e' semplicemente assente.
  // Simmetrico al branch USER_STATE_ACTIVE qui sotto.
  if (user.state === "USER_STATE_INITIAL") {
    if (user.metadata?.invite_status === "pending")
      return { label: "Invitato", variant: "info", icon: "mail", desc: "L'account è pronto. L'utente può accedere tramite magic link dalla pagina di login." };
    return { label: "Attivo", variant: "success", icon: "check_circle", desc: "L'utente accede via magic link." };
  }
  if (user.state === "USER_STATE_ACTIVE" && user.metadata?.invite_status === "pending")
    return { label: "Invitato", variant: "info", icon: "mail", desc: "L'invito è stato inviato. L'utente non ha ancora effettuato il primo accesso." };
  if (user.state === "USER_STATE_ACTIVE")
    return { label: "Attivo", variant: "success", icon: "check_circle", desc: "L'utente è attivo e può accedere alla dashboard." };
  if (user.state === "USER_STATE_INACTIVE")
    return { label: "Disattivato", variant: "neutral", icon: "person_off", desc: "L'utente è stato disattivato da un amministratore." };
  if (user.state === "USER_STATE_LOCKED")
    return { label: "Bloccato", variant: "danger", icon: "lock", desc: "L'utente è stato bloccato per troppi tentativi falliti." };
  return { label: user.state, variant: "neutral", icon: "help", desc: "" };
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso); }
}

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user: caller } = useAuth();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Role editing
  const [editingRole, setEditingRole] = useState(false);
  const [editorValue, setEditorValue] = useState<RoleEditorValue>({ base: "operator", flows: [] });

  // Delete confirmation
  const [showDelete, setShowDelete] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const u = await adminGetUser(userId);
      setUser(u);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadTenants = useCallback(async () => {
    try { setTenants(await adminListTenants()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadUser(); loadTenants(); }, [loadUser, loadTenants]);

  const tenantName = tenants.find((t) => t.tenant_id === user?.tenant_id)?.name || user?.tenant_id || "—";

  const handleDeactivate = async () => {
    if (!user || !confirm(`Disattivare "${user.display_name || user.email}"?`)) return;
    setActionLoading(true);
    try {
      await adminDeactivateUser(user.user_id);
      toast.addToast("success", "Utente disattivato");
      loadUser();
    } catch (err) { toast.addToast("error", (err as Error).message); }
    finally { setActionLoading(false); }
  };

  const handleReactivate = async () => {
    if (!user) return;
    setActionLoading(true);
    try {
      await adminReactivateUser(user.user_id);
      toast.addToast("success", "Utente riattivato");
      loadUser();
    } catch (err) { toast.addToast("error", (err as Error).message); }
    finally { setActionLoading(false); }
  };

  const handleResendInvite = async () => {
    if (!user) return;
    setActionLoading(true);
    try {
      await adminResendInvite(user.user_id);
      toast.addToast("success", `Invito re-inviato a ${user.email}`);
    } catch (err) { toast.addToast("error", (err as Error).message); }
    finally { setActionLoading(false); }
  };

  const handleChangeRole = async () => {
    if (!user) return;
    const newRoles = rolesFromEditor(editorValue);
    if (newRoles.length === 0) return;
    setActionLoading(true);
    try {
      const [primary, ...extras] = newRoles;
      await adminChangeRole(user.user_id, primary, false);
      for (const r of extras) await adminChangeRole(user.user_id, r, true);
      toast.addToast("success", `Ruoli aggiornati`);
      setEditingRole(false);
      loadUser();
    } catch (err) { toast.addToast("error", (err as Error).message); }
    finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    if (!user) return;
    setActionLoading(true);
    try {
      await adminDeleteUser(user.user_id);
      toast.addToast("success", `Utente eliminato: ${user.email}`);
      navigate("/admin/users");
    } catch (err) { toast.addToast("error", (err as Error).message); }
    finally { setActionLoading(false); }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div>
        <button className="btn btn-ghost" onClick={() => navigate("/admin/users")}>← Utenti</button>
        <div className="card" style={{ marginTop: "1rem", borderLeft: "4px solid var(--indaco-danger)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
          {error || "Utente non trovato"}
        </div>
      </div>
    );
  }

  const st = stateInfo(user);
  const isSelf = !!caller?.sub && caller.sub === user.user_id;

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flow-breadcrumb">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate("/admin/users")}>← Utenti</button>
        <span>/</span>
        <span>{user.display_name || user.email}</span>
      </div>

      {/* Header with name and status */}
      <div className="card" style={{ marginBottom: "var(--space-4)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <div className="avatar avatar-lg">
              {(user.first_name?.[0] || "").toUpperCase()}{(user.last_name?.[0] || "").toUpperCase()}
            </div>
            <div>
              <h1 style={{ fontSize: "var(--font-size-xl)", fontWeight: 600, margin: 0 }}>
                {user.display_name || `${user.first_name} ${user.last_name}`}
              </h1>
              <div className="text-muted" style={{ fontSize: "var(--font-size-sm)" }}>{user.email}</div>
            </div>
          </div>
          <span className={`badge badge-${st.variant}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--font-size-sm)", padding: "var(--space-2) var(--space-3)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{st.icon}</span>
            {st.label}
          </span>
        </div>
        {st.desc && (
          <div className="text-sm text-muted" style={{ marginTop: "var(--space-3)", paddingLeft: "4.5rem" }}>{st.desc}</div>
        )}
      </div>

      {/* Info grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)", marginBottom: "var(--space-4)" }}>
        {/* Info card */}
        <div className="card">
          <h3 style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: "var(--space-4)", display: "flex", alignItems: "center", gap: 6 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-brand-primary)" }}>badge</span>
            Informazioni
          </h3>
          <div style={gridStyle}>
            <span className="text-muted fw-medium">Email:</span>
            <span>{user.email} {user.email_verified ? (
              <span style={{ color: "var(--indaco-success)", fontSize: "var(--font-size-xs)" }}>
                <span className="material-symbols-outlined icon-sm">check</span> verificata
              </span>
            ) : <span style={{ color: "var(--indaco-warning)", fontSize: "var(--font-size-xs)" }}>non verificata</span>}</span>

            <span className="text-muted fw-medium">Cliente:</span>
            <span>{tenantName}</span>

            {editingRole ? (
              <div style={{ gridColumn: "1 / -1" }}>
                <RoleEditor
                  value={editorValue}
                  onChange={setEditorValue}
                  tenants={tenants}
                  currentTenantId={user.metadata?.tenant_id ?? user.tenant_id ?? ""}
                  lockTenant
                />
                <div style={{ display: "flex", gap: 8, marginTop: "var(--space-3)" }}>
                  <button className="btn btn-primary btn-sm" disabled={actionLoading} onClick={handleChangeRole}>
                    {actionLoading ? <><span className="spinner" style={{ marginRight: 6 }} />Salvataggio…</> : "Salva"}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingRole(false)}>Annulla</button>
                </div>
              </div>
            ) : (
              <>
                <span className="text-muted fw-medium">Flussi / Ruoli:</span>
                <span>
                  <RoleDisplayBadges roles={user.roles ?? []} tenantModules={tenants.find(t => t.tenant_id === (user.metadata?.tenant_id ?? user.tenant_id))?.modules ?? []} />
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 4, padding: "0 4px" }}
                    onClick={() => {
                      setEditorValue(rolesFromEditor.parse(user.roles ?? []));
                      setEditingRole(true);
                    }}
                    title="Modifica ruoli"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
                  </button>
                </span>
              </>
            )}

            <span className="text-muted fw-medium">User ID:</span>
            <code style={{ fontSize: "var(--font-size-xs)" }}>{user.user_id}</code>
          </div>
        </div>

        {/* Timeline card */}
        <div className="card">
          <h3 style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: "var(--space-4)", display: "flex", alignItems: "center", gap: 6 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-brand-primary)" }}>timeline</span>
            Cronologia
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <TimelineItem
              icon="send"
              color="var(--indaco-info)"
              label="Invito inviato"
              date={user.created_at}
              done={!!user.created_at}
            />
            <TimelineItem
              icon="how_to_reg"
              color="var(--indaco-success)"
              label="Primo accesso completato"
              date={user.last_login || null}
              done={
                !!user.last_login ||
                // Gli utenti magic-link (USER_STATE_INITIAL) hanno già effettuato
                // l'accesso se invite_status NON è più "pending" — al primo login
                // riuscito il metadata viene rimosso, non impostato a "active"
                // (vedi _clear_invite_status in magic_link.py). Zitadel non registra
                // last_login per il login via OTP email.
                (user.state === "USER_STATE_INITIAL" && user.metadata?.invite_status !== "pending")
              }
            />
            <TimelineItem
              icon="login"
              color="var(--indaco-brand-primary)"
              label="Ultimo login"
              date={user.last_login}
              done={!!user.last_login}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="card">
        <h3 style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: "var(--space-4)" }}>Azioni</h3>
        <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
          {user.metadata?.invite_status === "pending" && (
            <button className="btn btn-primary" disabled={actionLoading} onClick={handleResendInvite}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>send</span>
              Re-invia invito
            </button>
          )}
          {user.state === "USER_STATE_ACTIVE" && (
            <button className="btn btn-warning" disabled={actionLoading} onClick={handleDeactivate}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>person_off</span>
              Disattiva
            </button>
          )}
          {user.state === "USER_STATE_INACTIVE" && (
            <button className="btn btn-success" disabled={actionLoading} onClick={handleReactivate}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>person</span>
              Riattiva
            </button>
          )}
          <button
            className="btn btn-danger"
            disabled={actionLoading || isSelf}
            title={isSelf ? "Non puoi eliminare te stesso" : undefined}
            onClick={() => { setShowDelete(true); setDeleteInput(""); }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>delete</span>
            Elimina utente
          </button>
        </div>
      </div>

      {/* Delete confirmation */}
      {showDelete && (
        <div className="dialog-overlay" onClick={() => setShowDelete(false)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 style={{ color: "var(--indaco-danger)" }}>
                <span className="material-symbols-outlined" style={{ fontSize: 22, marginRight: 6, verticalAlign: "middle" }}>warning</span>
                Elimina utente
              </h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDelete(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <p style={{ margin: "0 0 1rem" }}>
              Stai per eliminare <strong>{user.display_name || user.email}</strong> permanentemente.
              Questa azione è <strong>irreversibile</strong>.
            </p>
            <div className="form-group">
              <label className="form-label">Scrivi <code>{user.email}</code> per confermare:</label>
              <input className="form-input" value={deleteInput} onChange={(e) => setDeleteInput(e.target.value)} placeholder={user.email} autoFocus />
            </div>
            <div className="dialog-footer">
              <button className="btn btn-ghost" onClick={() => setShowDelete(false)}>Annulla</button>
              <button className="btn btn-danger" disabled={deleteInput !== user.email || actionLoading} onClick={handleDelete}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>delete_forever</span>
                Elimina definitivamente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Timeline item ── */

function TimelineItem({ icon, color, label, date, done }: {
  icon: string; color: string; label: string; date: string | null | undefined; done: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", opacity: done ? 1 : 0.4 }}>
      <span className="material-symbols-outlined" style={{ fontSize: 20, color: done ? color : "var(--indaco-gray-400)" }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div className="text-sm fw-medium">{label}</div>
        <div className="text-xs text-muted">{done ? formatDate(date) : "In attesa"}</div>
      </div>
      {done && <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-success)" }}>check</span>}
    </div>
  );
}

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gap: "var(--space-2) var(--space-4)",
  fontSize: "var(--font-size-sm)",
};
